import React, { useState } from 'react';
import type { Project } from '../../types/portal';
import { createShoot, deleteShoot, updateShoot, type ShootDraft } from '../../utils/projectShoots';
import { BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';
export interface ShootEdit {
  /** Absent while adding. */
  id?: string;
  projectId: string;
  draft: ShootDraft;
}
interface Props {
  projects: Project[];
  edit: ShootEdit;
  onSaved: () => void;
  onCancel: () => void;
}

/**
 * Add, change or remove one shoot day.
 *
 * The end date is the only field that needs explaining, so it explains
 * itself: leaving it empty means a single day, which is what most shoots
 * are. "Recording" and its like go in the name so a build day and the shoot
 * itself are not two identical entries a week apart.
 */
export function ShootEditor({
  projects,
  edit,
  onSaved,
  onCancel
}: Props) {
  const [projectId, setProjectId] = useState(edit.projectId);
  const [draft, setDraft] = useState<ShootDraft>(edit.draft);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');
  const existing = Boolean(edit.id);
  const project = projects.find(item => item.id === projectId);
  function set(patch: Partial<ShootDraft>) {
    setDraft(current => ({
      ...current,
      ...patch
    }));
  }
  async function save(event: React.FormEvent) {
    event.preventDefault();
    setFailed('');
    if (!projectId) return setFailed('Choose which project this shoot belongs to.');
    if (!draft.date) return setFailed('A shoot day needs a date.');
    if (draft.endDate && draft.endDate < draft.date) {
      return setFailed('The last day cannot be before the first.');
    }
    setBusy(true);
    const result = edit.id ? await updateShoot(edit.id, draft) : (await createShoot(projectId, draft)).result;
    setBusy(false);
    if (!result.ok) return setFailed(result.error ?? 'The shoot day could not be saved.');
    onSaved();
  }
  async function remove() {
    if (!edit.id) return;
    setBusy(true);
    setFailed('');
    const result = await deleteShoot(edit.id);
    setBusy(false);
    if (!result.ok) return setFailed(result.error ?? 'The shoot day could not be removed.');
    onSaved();
  }
  return <form onSubmit={save} className="mt-10 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-1419" data-mp="1mslycgc6q356">
      <h3 className="font-display text-2xl leading-none" data-id="element-1420" data-mp="1quz2j0ba9szu">
        {existing ? 'Edit Shoot Day' : 'Add Shoot Day'}
      </h3>

      <div className="mt-8 grid grid-cols-1 gap-7 sm:grid-cols-2" data-id="element-1421" data-mp="u5hnhuqeuulh">
        <label className="block" data-id="element-1422" data-mp="z31abj16cfp1">
          <span className="label block text-paper/40" data-id="element-1423" data-mp="z42qu30ylue5">Project</span>
          {existing ? (
        /* Moving a shoot between projects is a different operation from
           editing one, and doing it silently through a dropdown is how
           a shoot ends up on the wrong client. Remove and re-add. */
        <p className="mt-3 border-b border-paper/25 pb-2 font-sans text-sm tracking-wide2 text-paper" data-id="element-1424" data-mp="bdgiean1m83m">
              {project ? `${project.client} · ${project.name}` : 'Unknown project'}
            </p>) : <select value={projectId} onChange={event => setProjectId(event.target.value)} className={SELECT} data-id="element-1425" data-mp="2wojpw2kdq7x">
              <option value="" className={OPTION} data-id="element-1426" data-mp="3uynrh5vqzu4z">
                Choose a project…
              </option>
              {projects.map(item => <option key={item.id} value={item.id} className={OPTION} data-id="element-1427" data-mp="3l2uitxl5gksh">
                  {item.client} · {item.name}
                </option>)}
            </select>}
        </label>

        <label className="block" data-id="element-1428" data-mp="3bbq3uxvh4zxn">
          <span className="label block text-paper/40" data-id="element-1429" data-mp="3bd144nppnpk0">What is this day?</span>
          <input value={draft.title} onChange={event => set({
          title: event.target.value
        })} placeholder="e.g. Build + interviews" className={FIELD} data-id="element-1430" data-mp="37bxqm7v4y5ly" />
        </label>

        <label className="block" data-id="element-1431" data-mp="2xdiaezxncgr8">
          <span className="label block text-paper/40" data-id="element-1432" data-mp="119ls2mrvwqx9">Date</span>
          <input type="date" value={draft.date} onChange={event => set({
          date: event.target.value
        })} className={FIELD} data-id="element-1433" data-mp="11adjdleg82hq" />
        </label>

        <label className="block" data-id="element-1434" data-mp="rjindpbehoj3">
          <span className="label block text-paper/40" data-id="element-1435" data-mp="wilsf9rdrur2">Last Day (leave empty for one day)</span>
          <input type="date" value={draft.endDate ?? ''} min={draft.date || undefined} onChange={event => set({
          endDate: event.target.value
        })} className={FIELD} data-id="element-1436" data-mp="ij3zci71xvbw" />
        </label>

        <label className="block" data-id="element-1437" data-mp="8m7warzxa1eq">
          <span className="label block text-paper/40" data-id="element-1438" data-mp="8n9ctbzpjg3u">Call Time (optional)</span>
          <input value={draft.time} onChange={event => set({
          time: event.target.value
        })} placeholder="e.g. 10:00am - 2:00pm" className={FIELD} data-id="element-1439" data-mp="dk9hpjktmctd" />
        </label>

        <label className="block" data-id="element-1440" data-mp="3sg2sy6hkut8z">
          <span className="label block text-paper/40" data-id="element-1441" data-mp="3sf9x40k5po5m">Location (optional)</span>
          <input value={draft.location} onChange={event => set({
          location: event.target.value
        })} placeholder="e.g. DWTC Za'abeel Hall 4" className={FIELD} data-id="element-1442" data-mp="3nh8cvcazvisk" />
        </label>
      </div>

      {failed ? <p role="alert" className="label-sm mt-7 text-paper/60" data-id="element-1443" data-mp="17oxzf8anf7n">
          {failed}
        </p> : null}

      <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-1444" data-mp="f2zsgrimlybh">
        <button type="submit" disabled={busy} className={BUTTON_SOLID} data-id="element-1445" data-mp="a1k52ssz7fpx">
          {busy ? 'Saving…' : existing ? 'Save Shoot Day' : 'Add Shoot Day'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={BUTTON_QUIET} data-id="element-1446" data-mp="jzq2ax3u7xog">
          Cancel
        </button>
        {existing && <button type="button" onClick={remove} disabled={busy} className="label ml-auto text-paper/35 transition-colors duration-150 ease-out hover:text-paper" data-id="element-1447" data-mp="2qqzatkalbuzj">
            Remove
          </button>}
      </div>
    </form>;
}